import React, { FC } from 'react';
import { Box, Button, CircularProgress, Fade, Link, TextField, Typography } from '@mui/material';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import { palette } from '../../theme';

const GUARDIAN_ORANGE = '#E06A10';

export interface ConnectAthleteStepProps {
  athleteEmail: string;
  hasError: boolean;
  isLoading: boolean;
  onEmailChange: (v: string) => void;
  onRequest: () => void;
  onTryAgain: () => void;
  onSkip: () => void;
  onBack: () => void;
}

const ConnectAthleteStep: FC<ConnectAthleteStepProps> = ({
  athleteEmail,
  hasError,
  isLoading,
  onEmailChange,
  onRequest,
  onTryAgain,
  onSkip,
  onBack,
}) => {
  if (isLoading) {
    return (
      <Fade in>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 320,
            gap: 2,
          }}
        >
          <CircularProgress size={48} sx={{ color: GUARDIAN_ORANGE }} />
          <Typography variant="body2" color="text.secondary">
            Sending your request…
          </Typography>
        </Box>
      </Fade>
    );
  }

  return (
    <Box>
      {/* Back link */}
      <Link
        component="button"
        onClick={onBack}
        underline="none"
        sx={{
          display: 'flex',
          alignItems: 'center',
          color: 'text.secondary',
          fontSize: 14,
          mb: 3,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <ChevronLeftIcon sx={{ fontSize: 18, mr: 0.25 }} />
        Back
      </Link>

      {/* Icon badge */}
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 2.5,
          backgroundColor: `${GUARDIAN_ORANGE}18`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
        }}
      >
        <PersonAddAlt1Icon sx={{ color: GUARDIAN_ORANGE, fontSize: 26 }} />
      </Box>

      <Typography variant="h5" fontWeight={700} mb={0.5}>
        Connect to Your Athlete
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} sx={{ lineHeight: 1.6 }}>
        Enter their email to request guardian access to their account.
      </Typography>

      {/* Email field */}
      <TextField
        label="Athlete's Email"
        placeholder="athlete@example.com"
        type="email"
        value={athleteEmail}
        onChange={(e) => onEmailChange(e.target.value)}
        fullWidth
        size="small"
        error={hasError}
        helperText={hasError ? 'No account found with this email' : ''}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !hasError) onRequest();
        }}
        sx={{
          mb: hasError ? 1.5 : 3,
          '& label': { color: GUARDIAN_ORANGE, fontWeight: 600, fontSize: 13 },
          '& label.Mui-focused': { color: GUARDIAN_ORANGE },
          '& .MuiOutlinedInput-root': { borderRadius: '8px' },
          '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: hasError ? palette.error.main : GUARDIAN_ORANGE,
          },
        }}
      />

      {/* Error info box */}
      {hasError && (
        <Fade in>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              p: 2,
              borderRadius: 2,
              backgroundColor: `${GUARDIAN_ORANGE}10`,
              border: `1px solid ${GUARDIAN_ORANGE}40`,
              mb: 3,
            }}
          >
            <ErrorOutlineIcon sx={{ color: GUARDIAN_ORANGE, fontSize: 20, flexShrink: 0, mt: 0.1 }} />
            <Box>
              <Typography variant="body2" fontWeight={700} sx={{ color: GUARDIAN_ORANGE, mb: 0.25 }}>
                Couldn&apos;t find your athlete
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.5 }}>
                Make sure your athlete has already created a CoeLab account, and double-check the email address for
                typos.
              </Typography>
            </Box>
          </Box>
        </Fade>
      )}

      {/* Buttons */}
      {hasError ? (
        <>
          <Button
            variant="contained"
            fullWidth
            onClick={onTryAgain}
            sx={{
              borderRadius: 999,
              py: 1.25,
              fontWeight: 600,
              fontSize: 15,
              mb: 1.5,
              backgroundColor: GUARDIAN_ORANGE,
              boxShadow: 'none',
              '&:hover': { backgroundColor: GUARDIAN_ORANGE, filter: 'brightness(0.9)', boxShadow: 'none' },
            }}
          >
            Try Again
          </Button>
          <Button
            variant="outlined"
            fullWidth
            onClick={onSkip}
            sx={{
              borderRadius: 999,
              py: 1.25,
              fontWeight: 600,
              fontSize: 15,
              borderColor: GUARDIAN_ORANGE,
              color: GUARDIAN_ORANGE,
              '&:hover': { borderColor: GUARDIAN_ORANGE, backgroundColor: `${GUARDIAN_ORANGE}0A` },
            }}
          >
            Create an Account for Them
          </Button>
        </>
      ) : (
        <Button
          variant="contained"
          fullWidth
          onClick={onRequest}
          sx={{
            borderRadius: 999,
            py: 1.25,
            fontWeight: 600,
            fontSize: 15,
            backgroundColor: GUARDIAN_ORANGE,
            boxShadow: 'none',
            '&:hover': { backgroundColor: GUARDIAN_ORANGE, filter: 'brightness(0.9)', boxShadow: 'none' },
          }}
        >
          Request Guardian Access
        </Button>
      )}
    </Box>
  );
};

export default ConnectAthleteStep;
